import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { timestampMs } from "@/lib/datetime";

// Interfaces to match the log structure
interface TTFBLogEntry {
  date_time: string;
  service_name: string;
  ttfb: number;
}

interface EventLogEntry {
  date_time: string;
  event: string;
}

interface NewCallLog {
  ttfb: TTFBLogEntry[];
  events: EventLogEntry[];
}

interface CallLogsProps {
  callLogs: NewCallLog | null;
  loading: boolean;
}

const CallLogs: React.FC<CallLogsProps> = ({ callLogs, loading }) => {
  const [eventSearchTerm, setEventSearchTerm] = useState("");
  const [ttfbSearchTerm, setTtfbSearchTerm] = useState("");
  const [ttfbSortOrder, setTtfbSortOrder] = useState<"asc" | "desc">("desc");
  const [ttfbSortField, setTtfbSortField] = useState<"ttfb" | "timestamp">(
    "timestamp"
  );

  // Filter event logs based on search term
  const filteredEvents = useMemo(() => {
    if (!callLogs?.events) return [];

    return callLogs.events.filter(
      (log) =>
        log.event.toLowerCase().includes(eventSearchTerm.toLowerCase()) ||
        log.date_time.toLowerCase().includes(eventSearchTerm.toLowerCase())
    );
  }, [callLogs?.events, eventSearchTerm]);

  // Filter TTFB entries based on search term
  const filteredTtfb = useMemo(() => {
    if (!callLogs?.ttfb) return [];

    const filtered = callLogs.ttfb.filter(
      (entry) =>
        entry.date_time.toLowerCase().includes(ttfbSearchTerm.toLowerCase()) ||
        entry.service_name.toLowerCase().includes(ttfbSearchTerm.toLowerCase())
    );

    // Sort by selected field
    return [...filtered].sort((a, b) => {
      if (ttfbSortField === "ttfb") {
        const ttfbA = a.ttfb;
        const ttfbB = b.ttfb;
        return ttfbSortOrder === "asc" ? ttfbA - ttfbB : ttfbB - ttfbA;
      } else {
        // Sort by timestamp
        const timeA = timestampMs(a.date_time);
        const timeB = timestampMs(b.date_time);
        return ttfbSortOrder === "asc" ? timeA - timeB : timeB - timeA;
      }
    });
  }, [callLogs?.ttfb, ttfbSearchTerm, ttfbSortOrder, ttfbSortField]);

  if (loading) {
    return <div className="text-muted-foreground font-bold">Loading logs...</div>;
  }

  if (!callLogs) {
    return <div className="text-muted-foreground">No logs available</div>;
  }

  return (
    <Accordion type="multiple" defaultValue={["ttfb", "events"]}>
      <AccordionItem value="ttfb">
        <AccordionTrigger>
          <div className="flex items-center justify-between flex-1">
            <span className="text-sm font-bold">
              Time to First Byte (TTFB) Metrics
            </span>
            <span className="ml-2 text-xs bg-border px-2 py-1 rounded-full">
              {ttfbSearchTerm
                ? `${filteredTtfb?.length}/${callLogs?.ttfb?.length}`
                : callLogs?.ttfb?.length}
            </span>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <div className="mb-4">
            <div className="mb-2 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <Input
                type="text"
                placeholder="Search TTFB entries..."
                value={ttfbSearchTerm}
                onChange={(e) => setTtfbSearchTerm(e.target.value)}
                className="w-full md:w-64"
              />
              <div className="flex flex-wrap items-center gap-2 md:gap-4">
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setTtfbSortField("ttfb");
                      setTtfbSortOrder(
                        ttfbSortOrder === "asc" ? "desc" : "asc"
                      );
                    }}
                    className={`flex items-center gap-1 ${
                      ttfbSortField === "ttfb" ? "bg-muted" : ""
                    }`}
                  >
                    Sort by TTFB{" "}
                    {ttfbSortField === "ttfb" &&
                      (ttfbSortOrder === "asc" ? "↑" : "↓")}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setTtfbSortField("timestamp");
                      setTtfbSortOrder(
                        ttfbSortOrder === "asc" ? "desc" : "asc"
                      );
                    }}
                    className={`flex items-center gap-1 ${
                      ttfbSortField === "timestamp" ? "bg-muted" : ""
                    }`}
                  >
                    Sort by Time{" "}
                    {ttfbSortField === "timestamp" &&
                      (ttfbSortOrder === "asc" ? "↑" : "↓")}
                  </Button>
                </div>
                <div className="flex flex-wrap items-center gap-y-1 space-x-1">
                  <span className="text-xs text-muted-foreground">Response time:</span>
                  <span className="h-2 w-2 rounded-full bg-success ml-2"></span>
                  <span className="text-xs text-muted-foreground">
                    Fast (&lt;300ms)
                  </span>
                  <span className="h-2 w-2 rounded-full bg-warning ml-2"></span>
                  <span className="text-xs text-muted-foreground">
                    Medium (&lt;500ms)
                  </span>
                  <span className="h-2 w-2 rounded-full bg-destructive ml-2"></span>
                  <span className="text-xs text-muted-foreground">Slow (≥500ms)</span>
                </div>
              </div>
            </div>
            <ScrollArea className="h-[60dvh] md:h-[400px] w-full rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-1/4">Date/Time</TableHead>
                    <TableHead className="w-1/4">Service</TableHead>
                    <TableHead className="w-2/4">TTFB (ms)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTtfb.map((entry, index) => {
                    // Convert all values to milliseconds
                    const ttfbInMs = entry.ttfb * 1000;
                    // Find max TTFB for scaling bars
                    const maxTtfb = Math.max(
                      ...filteredTtfb.map((e) => e.ttfb * 1000)
                    );
                    // Calculate relative width percentage with a minimum of 5%
                    const barWidth = Math.max(5, (ttfbInMs / maxTtfb) * 100);

                    // Determine color based on response time
                    const getBarColor = () => {
                      if (ttfbInMs < 100) return "bg-success"; // Very fast: < 100ms
                      if (ttfbInMs < 300) return "bg-success"; // Fast: < 300ms
                      if (ttfbInMs < 500) return "bg-warning"; // Medium: < 500ms
                      return "bg-destructive"; // Slow: >= 500ms
                    };

                    return (
                      <TableRow
                        key={index}
                        className={`hover:bg-row-hover ${
                          index % 2 === 0 ? "bg-row-hover/50" : "bg-card"
                        }`}
                        title={`Response time: ${ttfbInMs.toFixed(2)}ms`}
                      >
                        <TableCell className="font-mono text-xs whitespace-nowrap">
                          {entry.date_time}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                              entry.service_name.includes("OpenAI")
                                ? "bg-success/10 text-success"
                                : entry.service_name.includes("ElevenLabs")
                                ? "bg-info/10 text-info"
                                : entry.service_name.includes("Deepgram")
                                ? "bg-accent text-primary"
                                : "bg-muted text-foreground"
                            }`}
                          >
                            {entry.service_name}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col w-full">
                            <div className="flex justify-between mb-1">
                              <span className="text-xs font-mono font-medium">
                                {ttfbInMs.toFixed(2)}ms
                              </span>
                            </div>
                            <div className="w-full h-1 bg-muted rounded-full">
                              <div
                                className={`h-full rounded-full ${getBarColor()} transition-all duration-300`}
                                style={{ width: `${barWidth}%` }}
                              ></div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="events">
        <AccordionTrigger>
          <div className="flex items-center justify-between flex-1">
            <span className="text-sm font-bold">Event Logs</span>
            <span className="ml-2 text-xs bg-border px-2 py-1 rounded-full">
              {eventSearchTerm
                ? `${filteredEvents?.length}/${callLogs?.events?.length}`
                : callLogs?.events?.length}
            </span>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <div className="mb-4">
            <Input
              type="text"
              placeholder="Search events..."
              value={eventSearchTerm}
              onChange={(e) => setEventSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>
          <ScrollArea className="h-[60dvh] md:h-[400px] w-full max-w-full rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-1/4">Date/Time</TableHead>
                  <TableHead>Event</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEvents.map((log, index) => (
                  <TableRow
                    key={index}
                    className={`hover:bg-row-hover ${
                      index % 2 === 0 ? "bg-row-hover/50" : "bg-card"
                    }`}
                  >
                    <TableCell className="font-mono text-xs whitespace-nowrap">
                      {log.date_time}
                    </TableCell>
                    <TableCell className="font-mono text-xs whitespace-pre-wrap break-all md:break-words">
                      {log.event.includes(" - ") ? (
                        <>
                          <span className="text-muted-foreground/60 font-mono text-xs">
                            {log.event.split(" - ")[0]}
                          </span>
                          <span className="ml-2 pl-2 border-border">
                            {log.event.split(" - ").slice(1).join(" - ")}
                          </span>
                        </>
                      ) : (
                        <span>{log.event}</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default CallLogs;
